import { Empty } from 'antd';
import { useMemo } from 'react';
import ReactECharts, { AXIS_TEXT, GRID_LINE } from '../charts/echarts';
import type { Mover } from '../contract';
import { COLOR_DOWN, COLOR_UP, creatorName, fmtKc, fmtPct } from '../format';
import styles from '../style.module.scss';

interface Props {
  title: string;
  rows: Mover[];
  direction: 'up' | 'down';
  onSelect?: (uid: string) => void;
}

const ROW_HEIGHT = 30;

/** Top tăng / Top giảm 7 ngày: thanh ngang theo % (nhãn "▲ +12,3%" để không chỉ dựa vào màu). */
const TopBars = ({ title, rows, direction, onSelect }: Props) => {
  const color = direction === 'up' ? COLOR_UP : COLOR_DOWN;
  const arrow = direction === 'up' ? '▲' : '▼';

  const option = useMemo(() => {
    // Trục y của echarts vẽ từ dưới lên: đảo để dòng lớn nhất nằm trên cùng.
    const list = rows.slice().reverse();
    return {
      animation: false,
      grid: { left: 4, right: 64, top: 4, bottom: 4, containLabel: true },
      tooltip: {
        trigger: 'item',
        formatter: (p: any) => {
          const r = list[p.dataIndex];
          if (!r) return '';
          return `${creatorName(r)}${r.groupName ? ` · ${r.groupName}` : ''}<br/>7 ngày: ${fmtPct(
            r.delta7Pct
          )}<br/>KC tháng: ${fmtKc(r.diamonds)}`;
        },
      },
      xAxis: {
        type: 'value',
        show: false,
        splitLine: { lineStyle: { color: GRID_LINE } },
      },
      yAxis: {
        type: 'category',
        data: list.map((r) => creatorName(r)),
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: { color: AXIS_TEXT, width: 120, overflow: 'truncate' },
      },
      series: [
        {
          type: 'bar',
          data: list.map((r) => Math.abs(r.delta7Pct)),
          barMaxWidth: 16,
          itemStyle: { color, borderRadius: [0, 4, 4, 0] },
          label: {
            show: true,
            position: 'right',
            color,
            fontWeight: 600,
            formatter: (p: any) => `${arrow} ${fmtPct(list[p.dataIndex]?.delta7Pct)}`,
          },
          cursor: onSelect ? 'pointer' : 'default',
        },
      ],
    };
  }, [rows, color, arrow, onSelect]);

  const onEvents = useMemo(
    () => ({
      click: (p: any) => {
        const list = rows.slice().reverse();
        const r = list[p.dataIndex];
        if (r && onSelect) onSelect(r.uid);
      },
    }),
    [rows, onSelect]
  );

  return (
    <section className={styles.card} aria-label={title}>
      <h2 className={styles.cardTitle}>{title}</h2>
      {rows.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Chưa có Creator đủ điều kiện (cần từ 10.000 KC trong 7 ngày gần nhất)."
        />
      ) : (
        <ReactECharts
          option={option}
          onEvents={onEvents}
          style={{ height: Math.max(120, rows.length * ROW_HEIGHT + 16), width: '100%' }}
        />
      )}
    </section>
  );
};

export default TopBars;
